// ---------------------------------------------------------------------------
// The rules, in one column.
//
// The numbers here are the ones the engine actually applies, so they are read
// from @countdown/shared rather than typed out: a house-rules change to the
// scoring table must not leave this page quietly lying about it.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CONUNDRUM_POINTS,
  LETTERS_MIN_CONSONANTS,
  LETTERS_MIN_VOWELS,
  LETTERS_NINE_BONUS_MULTIPLIER,
  LETTERS_TILE_COUNT,
  NUMBERS_SCORING,
  NUMBERS_TILE_COUNT,
} from '@countdown/shared';
import { AppHeader } from '../components/layout/AppHeader.js';

const NINE_SCORE = LETTERS_TILE_COUNT * LETTERS_NINE_BONUS_MULTIPLIER;

/** `exact 10, within 5 → 7, within 10 → 5`, straight from the scoring table. */
function numbersScoringLines(): string[] {
  return NUMBERS_SCORING.map((band) =>
    band.withinInclusive === 0
      ? `Hit the target exactly and you score ${band.points}.`
      : `Within ${band.withinInclusive} scores ${band.points}.`,
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[24px] font-bold tracking-[-0.02em]">{title}</h2>
      {children}
    </section>
  );
}

export function HowToPlayScreen() {
  return (
    <div className="flex min-h-full flex-col">
      <AppHeader />

      <main className="mx-auto flex w-full max-w-[64ch] flex-col gap-9 px-4 py-10 sm:px-8 sm:py-14">
        <header className="flex flex-col gap-3">
          <p className="label-mono">how to play</p>
          <h1 className="text-[38px] font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-[48px]">
            Nine tiles, thirty seconds.
          </h1>
          <p className="text-[18px] leading-relaxed text-ink-muted">
            A game is any mix of three round types, in any order, as long as you like.
          </p>
        </header>

        <Section title="Letters">
          <p className="text-[17px] leading-relaxed text-ink-muted">
            One player calls vowel or consonant {LETTERS_TILE_COUNT} times, on a shared thirty-second
            budget, with at least {LETTERS_MIN_VOWELS} vowels and {LETTERS_MIN_CONSONANTS}{' '}
            consonants. Everyone then has thirty seconds to build the longest word they can. Each
            tile once.
          </p>
          <p className="text-[17px] leading-relaxed text-ink-muted">
            A word scores its own length. All {LETTERS_TILE_COUNT} is worth {NINE_SCORE}, double.
          </p>
          <p className="text-[17px] leading-relaxed text-ink-muted">
            Lengths stay private until the clock stops. Locking in freezes your answer, not the
            clock.
          </p>
        </Section>

        <Section title="Numbers">
          <p className="text-[17px] leading-relaxed text-ink-muted">
            {NUMBERS_TILE_COUNT} tiles. One player picks how many come from 25, 50, 75 and 100; the
            rest are 1 to 10. Reach the three-digit target in steps, in thirty seconds. Whole numbers
            only, nothing at or below zero, each tile once.
          </p>
          <ul className="flex flex-col gap-1.5 border-l-2 border-line pl-4 text-[17px] leading-relaxed text-ink-muted">
            {numbersScoringLines().map((line) => (
              <li key={line}>{line}</li>
            ))}
            <li>Further away than that scores nothing.</li>
          </ul>
          <p className="text-[17px] leading-relaxed text-ink-muted">
            The screen never says whether you are right, or how close. You read your own working.
          </p>
        </Section>

        <Section title="Conundrum">
          <p className="text-[17px] leading-relaxed text-ink-muted">
            {LETTERS_TILE_COUNT} scrambled letters conceal one nine-letter word. Buzzing pauses the
            clock for everyone and gives you ten seconds. A wrong answer puts you out for the rest of
            the round. Solving it is worth {CONUNDRUM_POINTS}.
          </p>
        </Section>

        <Section title="Ties and teams">
          <p className="text-[17px] leading-relaxed text-ink-muted">
            Players level on points share the win. There is no tie-break.
          </p>
          <p className="text-[17px] leading-relaxed text-ink-muted">
            A team scores best-of rather than a sum: its round score is its highest-scoring
            member&rsquo;s answer.
          </p>
        </Section>

        <Section title="Watching">
          <p className="text-[17px] leading-relaxed text-ink-muted">
            Spectators play along, and their answers never enter the scoring or the reveal.
          </p>
        </Section>

        <p className="text-[15px] leading-relaxed text-ink-dim">
          Timers, the round mix, the player limit and the dictionary band are all room settings.
        </p>

        <Link to="/" className="w-fit text-[16px] font-semibold text-blue-200">
          Back home
        </Link>
      </main>
    </div>
  );
}
